/**
 * PHASE 6: OfflineDebugPanel
 * Debug/test panel for offline sync development and QA
 * 
 * Features:
 * - Network status display
 * - Backend reachability check
 * - Queue item count and recent logs
 * - Force sync and refresh actions
 * - Simulate offline mode
 */

import { useState, useEffect, useCallback } from 'react';
import { 
  Bug, 
  Wifi, 
  WifiOff, 
  RefreshCw, 
  Server, 
  Database,
  Clock,
  Trash2,
  Play,
  Pause,
  AlertCircle,
  CheckCircle,
  XCircle,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useOfflineSync } from '@/hooks/useOfflineSync';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { 
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger
} from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Fetch backend health status
 */
async function fetchBackendHealth() {
  try {
    const response = await fetch(`${API_URL}/api/health`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    });
    
    if (!response.ok) {
      return { ok: false, error: `HTTP ${response.status}` };
    }
    
    const data = await response.json();
    return { ok: true, data };
  } catch (error) {
    return { ok: false, error: error.message };
  }
}

/**
 * Fetch backend ready status
 */
async function fetchBackendReady() {
  try {
    const response = await fetch(`${API_URL}/api/ready`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    });
    
    if (!response.ok) {
      return { ok: false, error: `HTTP ${response.status}` };
    }
    
    const data = await response.json();
    return { ok: true, data };
  } catch (error) {
    return { ok: false, error: error.message };
  }
}

/**
 * Status indicator component
 */
function StatusIndicator({ status, label }) {
  const statusConfig = {
    ok: { icon: CheckCircle, color: 'text-green-500', bg: 'bg-green-100' },
    error: { icon: XCircle, color: 'text-red-500', bg: 'bg-red-100' },
    warning: { icon: AlertCircle, color: 'text-amber-500', bg: 'bg-amber-100' },
    loading: { icon: RefreshCw, color: 'text-blue-500', bg: 'bg-blue-100', spin: true }
  };
  
  const config = statusConfig[status] || statusConfig.error;
  const Icon = config.icon;
  
  return (
    <div className={cn("flex items-center gap-2 px-3 py-2 rounded-lg", config.bg)}>
      <Icon className={cn("h-4 w-4", config.color, config.spin && "animate-spin")} />
      <span className={cn("text-sm font-medium", config.color)}>{label}</span>
    </div>
  );
}

/**
 * Log entry component
 */
function LogEntry({ entry }) {
  const typeColors = {
    info: 'text-blue-600',
    success: 'text-green-600',
    error: 'text-red-600',
    warning: 'text-amber-600'
  };
  
  return (
    <div className="flex gap-2 py-1 text-xs font-mono border-b border-dashed last:border-0">
      <span className="text-muted-foreground w-20 shrink-0">
        {new Date(entry.timestamp).toLocaleTimeString()}
      </span>
      <span className={cn("flex-1", typeColors[entry.type] || 'text-foreground')}>
        {entry.message}
      </span>
    </div>
  );
}

/**
 * OfflineDebugPanel - Main component
 */
export function OfflineDebugPanel() {
  const {
    isOnline,
    isSyncing,
    pendingCount,
    failedCount,
    conflictCount,
    queueItems,
    syncNow,
    clearCompleted,
    refreshStats
  } = useOfflineSync();

  const [backendHealth, setBackendHealth] = useState(null);
  const [backendReady, setBackendReady] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [simulateOffline, setSimulateOffline] = useState(false);
  const [expanded, setExpanded] = useState({
    backend: true,
    queue: true,
    logs: false
  });

  // Add log entry
  const addLog = useCallback((type, message) => {
    setLogs(prev => [{
      id: Date.now(),
      timestamp: Date.now(),
      type,
      message
    }, ...prev].slice(0, 50));
  }, []);

  // Refresh backend status
  const refreshBackendStatus = useCallback(async () => {
    setIsLoading(true);
    addLog('info', 'Checking backend status...');
    
    try {
      const [health, ready] = await Promise.all([
        fetchBackendHealth(),
        fetchBackendReady()
      ]);
      
      setBackendHealth(health);
      setBackendReady(ready);
      
      if (health.ok) {
        addLog('success', `Backend healthy: ${health.data?.db_type || 'unknown'} DB`);
      } else {
        addLog('error', `Backend health check failed: ${health.error}`);
      }
    } catch (error) {
      addLog('error', `Backend check error: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  }, [addLog]);

  // Initial load
  useEffect(() => {
    refreshBackendStatus();
    refreshStats();
  }, [refreshBackendStatus, refreshStats]);

  // Handle force sync
  const handleForceSync = async () => {
    addLog('info', 'Forcing sync...');
    try {
      const result = await syncNow();
      if (result.success) {
        addLog('success', 'Sync completed successfully');
      } else {
        addLog('warning', `Sync finished with issues: ${result.reason}`);
      }
    } catch (error) {
      addLog('error', `Sync failed: ${error.message}`);
    }
    await refreshStats();
  };

  // Handle clear completed
  const handleClearCompleted = async () => {
    addLog('info', 'Clearing completed items...');
    try {
      await clearCompleted();
      addLog('success', 'Completed items cleared');
    } catch (error) {
      addLog('error', `Clear failed: ${error.message}`);
    }
    await refreshStats();
  };

  // Simulate offline toggle
  const handleSimulateOffline = (checked) => {
    setSimulateOffline(checked);
    if (checked) {
      addLog('warning', 'Simulating offline mode (UI only)');
    } else {
      addLog('info', 'Offline simulation disabled');
    }
  };

  const effectiveOnline = simulateOffline ? false : isOnline;

  return (
    <div className="space-y-4 p-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Bug className="h-5 w-5 text-purple-600" />
          <h2 className="font-semibold text-lg">Offline Debug Panel</h2>
        </div>
        <Badge variant={effectiveOnline ? "default" : "destructive"}>
          {effectiveOnline ? "Online" : "Offline"}
        </Badge>
      </div>

      {/* Network Status */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-2 gap-3">
            <StatusIndicator 
              status={effectiveOnline ? 'ok' : 'error'} 
              label={effectiveOnline ? 'Network Online' : 'Network Offline'}
            />
            <StatusIndicator 
              status={isSyncing ? 'loading' : pendingCount > 0 ? 'warning' : 'ok'} 
              label={isSyncing ? 'Syncing...' : pendingCount > 0 ? `${pendingCount} Pending` : 'Queue Empty'}
            />
          </div>
          
          <div className="flex items-center justify-between mt-4 pt-4 border-t">
            <span className="text-sm text-muted-foreground">Simulate Offline</span>
            <Switch 
              checked={simulateOffline} 
              onCheckedChange={handleSimulateOffline}
            />
          </div>
        </CardContent>
      </Card>

      {/* Backend Status */}
      <Card>
        <CardHeader className="p-4 pb-2">
          <button 
            className="flex items-center justify-between w-full"
            onClick={() => setExpanded(p => ({ ...p, backend: !p.backend }))}
          >
            <CardTitle className="text-sm flex items-center gap-2">
              <Server className="h-4 w-4" />
              Backend Status
            </CardTitle>
            {expanded.backend ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
        </CardHeader>
        
        {expanded.backend && (
          <CardContent className="p-4 pt-0 space-y-3">
            {backendHealth ? (
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Health</span>
                  <Badge variant={backendHealth.data?.status === 'healthy' ? 'default' : 'secondary'}>
                    {backendHealth.data?.status || 'unknown'}
                  </Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Database</span>
                  <span className="font-mono text-xs">{backendHealth.data?.db_type || 'unknown'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Is Fallback</span>
                  <span>{backendHealth.data?.is_fallback ? 'Yes (SQLite)' : 'No'}</span>
                </div>
                {backendReady?.data?.db_info?.last_error && (
                  <div className="mt-2 p-2 rounded bg-red-50 dark:bg-red-900/20 text-xs text-red-700 dark:text-red-400">
                    <p className="font-medium">DB Error:</p>
                    <p className="truncate">{backendReady.data.db_info.last_error.split('\n')[0]}</p>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Loading...</p>
            )}
            
            <Button 
              variant="outline" 
              size="sm" 
              className="w-full"
              onClick={refreshBackendStatus}
              disabled={isLoading}
            >
              <RefreshCw className={cn("h-4 w-4 mr-2", isLoading && "animate-spin")} />
              Refresh Status
            </Button>
          </CardContent>
        )}
      </Card>

      {/* Queue Status */}
      <Card>
        <CardHeader className="p-4 pb-2">
          <button 
            className="flex items-center justify-between w-full"
            onClick={() => setExpanded(p => ({ ...p, queue: !p.queue }))}
          >
            <CardTitle className="text-sm flex items-center gap-2">
              <Database className="h-4 w-4" />
              Offline Queue
            </CardTitle>
            {expanded.queue ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
        </CardHeader>
        
        {expanded.queue && (
          <CardContent className="p-4 pt-0 space-y-3">
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="p-2 rounded bg-blue-50 dark:bg-blue-900/20">
                <p className="text-xl font-bold text-blue-600">{pendingCount}</p>
                <p className="text-xs text-muted-foreground">Pending</p>
              </div>
              <div className="p-2 rounded bg-red-50 dark:bg-red-900/20">
                <p className="text-xl font-bold text-red-600">{failedCount}</p>
                <p className="text-xs text-muted-foreground">Failed</p>
              </div>
              <div className="p-2 rounded bg-purple-50 dark:bg-purple-900/20">
                <p className="text-xl font-bold text-purple-600">{conflictCount}</p>
                <p className="text-xs text-muted-foreground">Conflicts</p>
              </div>
            </div>
            
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                className="flex-1"
                onClick={handleForceSync}
                disabled={!effectiveOnline || isSyncing || pendingCount === 0}
              >
                <Play className="h-4 w-4 mr-1" />
                Force Sync
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                className="flex-1"
                onClick={handleClearCompleted}
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Clear Done
              </Button>
            </div>
            
            {queueItems.length > 0 && (
              <div className="mt-2 space-y-1">
                <p className="text-xs font-medium text-muted-foreground">Recent Items:</p>
                {queueItems.slice(0, 3).map(item => (
                  <div key={item.id} className="flex items-center justify-between text-xs p-2 rounded bg-muted/50">
                    <span className="font-mono truncate">{item.type}</span>
                    <Badge variant="outline" className="text-[10px]">{item.status}</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Activity Log */}
      <Card>
        <CardHeader className="p-4 pb-2">
          <button 
            className="flex items-center justify-between w-full"
            onClick={() => setExpanded(p => ({ ...p, logs: !p.logs }))}
          >
            <CardTitle className="text-sm flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Activity Log ({logs.length})
            </CardTitle>
            {expanded.logs ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
        </CardHeader>
        
        {expanded.logs && (
          <CardContent className="p-4 pt-0">
            <ScrollArea className="h-40">
              {logs.length > 0 ? (
                logs.map(entry => <LogEntry key={entry.id} entry={entry} />)
              ) : (
                <p className="text-xs text-muted-foreground text-center py-4">No activity yet</p>
              )}
            </ScrollArea>
          </CardContent>
        )}
      </Card>
    </div>
  );
}

/**
 * OfflineDebugDrawer - Floating trigger button + drawer
 */
export function OfflineDebugDrawer() {
  const [open, setOpen] = useState(false);
  
  // Only show in development
  if (process.env.NODE_ENV === 'production') {
    return null;
  }
  
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          size="icon"
          variant="outline"
          className="fixed bottom-4 left-4 z-50 h-10 w-10 rounded-full shadow-lg bg-purple-100 hover:bg-purple-200 border-purple-300"
        >
          <Bug className="h-5 w-5 text-purple-600" />
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="w-full sm:max-w-md p-0">
        <SheetHeader className="p-4 border-b">
          <SheetTitle>Offline Debug Panel</SheetTitle>
        </SheetHeader>
        <ScrollArea className="h-[calc(100vh-80px)]">
          <OfflineDebugPanel />
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}

export default OfflineDebugPanel;
